import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../../hooks/redux';
import ProjectProductTagsModal from '../../../../../components/projectProductTagModal/ProjectProductTagsModal';
import {
    deleteProjectProductTagAction,
    getProjectProductTagsByProjectProductIdAction,
} from '../../../../../redux/actions/projectProductTagActions';
import { alertService } from '../../../../../services';

type ProjectProductTagsContextType = {
    open: (projectProductId: number) => void;
    close: () => void;
    deleteTag: (projectProductTagId: number) => void;
};

export const ProjectProductTagsContext = createContext<ProjectProductTagsContextType>({
    open: () => { },
    close: () => { },
    deleteTag: (_projectProductTagId: number) => { },
});

export const useProjectProductTags = () => useContext(ProjectProductTagsContext);

export const ProjectProductTagsProvider: React.FC<{ children: any; onSaved?: () => void }> = ({
    children,
    onSaved,
}) => {
    const dispatch = useAppDispatch();

    const [visible, setVisible] = useState(false);
    const [projectProductId, setProjectProductId] = useState<number | null>(null);

    const open = (id: number) => {
        setProjectProductId(id);
        setVisible(true);
        dispatch(getProjectProductTagsByProjectProductIdAction(id));
    };

    const close = () => {
        setVisible(false);
        setProjectProductId(null);
    };

    const handleDeleteProjectProductTag = async (projectProductTagId: number) => {
        if (!projectProductTagId) {
            return;
        }
        try {
            await dispatch(deleteProjectProductTagAction(projectProductTagId));
            alertService.success('Tag removed');
        } catch (e) {
            console.error(e);
            alertService.error('Failed to remove tag');
        }
    };

    const contextValue = useMemo(
        () => ({
            open,
            close,
            deleteTag: handleDeleteProjectProductTag,
        }),
        [open, close, handleDeleteProjectProductTag],
    );

    return (
        <ProjectProductTagsContext.Provider value={contextValue}>
            {children}
            <ProjectProductTagsModal
                visible={visible}
                projectProductId={projectProductId}
                onClose={close}
                onSaved={onSaved}
            />
        </ProjectProductTagsContext.Provider>
    );
};

export default ProjectProductTagsContext;
